// src/components/DocumentTreeView.tsx
// Tree view for the Document Workspace — indented list with expand/collapse.

import { useState, useCallback, useRef, useEffect } from 'react';
import { useI18n } from '@react/hooks';
import { Badge } from '@react/components';

import type { DocumentTreeNode, FlatTreeItem } from '@/features/document/types';
import {
  flattenDocumentTree,
  getDocumentIcon,
  getStatusMapping,
  documentStatusMap,
  formatRelativeTime,
} from '@/features/document/logic';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface DocumentTreeViewProps {
  tree: DocumentTreeNode[];
  selectedDocId?: string;
  onSelect: (id: string) => void;
  onOpen: (id: string) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DocumentTreeView({
  tree,
  selectedDocId,
  onSelect,
  onOpen,
}: DocumentTreeViewProps) {
  const { t, locale } = useI18n();
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());
  const [focusIndex, setFocusIndex] = useState(-1);
  const listRef = useRef<HTMLDivElement>(null);

  const flatItems = flattenDocumentTree(tree, expandedIds);

  const toggleExpand = useCallback((id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (flatItems.length === 0) return;

      switch (e.key) {
        case 'ArrowDown': {
          e.preventDefault();
          const next = Math.min(focusIndex + 1, flatItems.length - 1);
          setFocusIndex(next);
          onSelect(flatItems[next].id);
          break;
        }
        case 'ArrowUp': {
          e.preventDefault();
          const prev = Math.max(focusIndex - 1, 0);
          setFocusIndex(prev);
          onSelect(flatItems[prev].id);
          break;
        }
        case 'ArrowRight': {
          e.preventDefault();
          if (focusIndex >= 0 && focusIndex < flatItems.length) {
            const item = flatItems[focusIndex];
            if (item.hasChildren && !item.isExpanded) {
              toggleExpand(item.id);
            }
          }
          break;
        }
        case 'ArrowLeft': {
          e.preventDefault();
          if (focusIndex >= 0 && focusIndex < flatItems.length) {
            const item = flatItems[focusIndex];
            if (item.hasChildren && item.isExpanded) {
              toggleExpand(item.id);
            }
          }
          break;
        }
        case 'Enter': {
          e.preventDefault();
          if (focusIndex >= 0 && focusIndex < flatItems.length) {
            onOpen(flatItems[focusIndex].id);
          }
          break;
        }
      }
    },
    [flatItems, focusIndex, onSelect, onOpen, toggleExpand],
  );

  // Scroll focused item into view
  useEffect(() => {
    if (focusIndex < 0 || !listRef.current) return;
    const rows = listRef.current.querySelectorAll('[data-tree-row]');
    rows[focusIndex]?.scrollIntoView({ block: 'nearest' });
  }, [focusIndex]);

  if (flatItems.length === 0) {
    return (
      <div className="py-12 text-center">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
          <svg className="h-6 w-6 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
          </svg>
        </div>
        <p className="text-sm font-medium text-foreground">{t('document.emptyTree')}</p>
        <p className="mt-1 text-xs text-muted-foreground">{t('document.emptyTreeDescription')}</p>
      </div>
    );
  }

  return (
    // TODO: integrate @dnd-kit for drag & drop reordering of tree nodes
    <div
      ref={listRef}
      className="rounded-lg border border-border overflow-hidden"
      role="tree"
      tabIndex={0}
      onKeyDown={handleKeyDown}
      aria-label={t('document.treeView')}
    >
      {flatItems.map((item, index) => (
        <TreeRow
          key={item.id}
          item={item}
          isSelected={item.id === selectedDocId}
          isFocused={index === focusIndex}
          onSelect={() => {
            setFocusIndex(index);
            onSelect(item.id);
          }}
          onDoubleClick={() => onOpen(item.id)}
          onToggle={() => toggleExpand(item.id)}
          locale={locale}
          t={t}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tree row
// ---------------------------------------------------------------------------

interface TreeRowProps {
  item: FlatTreeItem;
  isSelected: boolean;
  isFocused: boolean;
  onSelect: () => void;
  onDoubleClick: () => void;
  onToggle: () => void;
  locale: string;
  t: (key: string) => string;
}

function TreeRow({
  item,
  isSelected,
  isFocused,
  onSelect,
  onDoubleClick,
  onToggle,
  locale,
  t,
}: TreeRowProps) {
  const statusInfo = getStatusMapping(documentStatusMap, item.status);
  const iconPath = getDocumentIcon({ type: item.type, is_folder: item.isFolder });

  return (
    <div
      data-tree-row
      role="treeitem"
      aria-selected={isSelected}
      aria-expanded={item.hasChildren ? item.isExpanded : undefined}
      aria-level={item.depth + 1}
      className={`flex items-center gap-2 px-2 py-1.5 cursor-pointer transition-colors select-none ${
        isSelected
          ? 'bg-primary/10 text-foreground'
          : 'text-foreground hover:bg-muted/50'
      } ${isFocused ? 'ring-1 ring-inset ring-primary/30' : ''}`}
      style={{ paddingLeft: `${item.depth * 24 + 8}px` }}
      onClick={onSelect}
      onDoubleClick={onDoubleClick}
    >
      {/* Expand/collapse chevron */}
      {item.hasChildren ? (
        <button
          type="button"
          onClick={e => {
            e.stopPropagation();
            onToggle();
          }}
          className="flex-shrink-0 rounded p-0.5 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          aria-label={item.isExpanded ? t('common.collapse') : t('common.expand')}
        >
          <svg
            className={`h-3.5 w-3.5 transition-transform ${item.isExpanded ? 'rotate-90' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      ) : (
        <span className="w-[18px] flex-shrink-0" />
      )}

      {/* Type/folder icon */}
      <svg
        className={`h-4 w-4 flex-shrink-0 ${item.isFolder ? 'text-primary' : 'text-muted-foreground'}`}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={1.5}
      >
        <path strokeLinecap="round" strokeLinejoin="round" d={iconPath} />
      </svg>

      {/* Title */}
      <span className="flex-1 truncate text-sm">
        {item.title || t('common.untitled')}
      </span>

      {/* Status badge */}
      {item.status && (
        <Badge variant={statusInfo.variant as any} size="sm">
          {t(statusInfo.labelKey)}
        </Badge>
      )}

      {/* Updated date */}
      {item.updated_at && (
        <span className="flex-shrink-0 text-xs text-muted-foreground whitespace-nowrap">
          {formatRelativeTime(item.updated_at, locale)}
        </span>
      )}
    </div>
  );
}

export default DocumentTreeView;
